import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { url_servicios } from 'src/app/config/config';
import {
  ImpersonationTokenResponse,
  ImpersonationStopResponse,
  ImpersonationState,
  ActiveImpersonationsResponse,
  ImpersonationError,
  ImpersonatedUser,
} from './impersonation.interface';
import { IMPERSONATION_CONSTS, IMPERSONATION_URLS } from './impersonation.const';
import { AuthService } from './auth.service';
import { AppRoutes } from '../../shared/routes/routes';
import { AuthUser } from './auth.interface';
import { LocationContextService } from '../services/location-context.service';

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly locationContextService = inject(LocationContextService);

  // State management
  private readonly impersonationStateSubject = new BehaviorSubject<ImpersonationState>({
    isActive: false,
    impersonationId: null,
    impersonatedUser: null,
    impersonatorUser: null,
    expiresAt: null,
    token: null,
  });

  public impersonationState$ = this.impersonationStateSubject.asObservable();

  // Timer para verificar expiración
  private expirationCheckInterval: ReturnType<typeof setInterval> | null = null;

  // Store original admin user to restore after impersonation ends
  private originalAdminUser: AuthUser | null = null;

  private setAuthUserAndEmit(user: AuthUser | null): void {
    this.authService.setUserAndEmit(user);
  }

  constructor() {
    // Restore impersonation state from sessionStorage on service initialization
    this.restoreImpersonationState();

    // Start expiration check interval
    this.startExpirationCheck();
  }

  ngOnDestroy(): void {
        this.stopExpirationCheck();
    }

  /**
   * Get current impersonation state
   */
  get state(): ImpersonationState {
    return this.impersonationStateSubject.value;
  }

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    return this.state.isActive;
  }

  /**
   * Get the impersonation token
   */
  getImpersonationToken(): string | null {
    if (!this.isImpersonating()) {
      return null;
    }
    return sessionStorage.getItem(IMPERSONATION_CONSTS.token);
  }

  /**
   * Get the effective user (impersonated user or current admin)
   */
  getEffectiveUser(): ImpersonatedUser | null {
    return this.state.impersonatedUser;
  }

  /**
   * Start impersonating a user
   */
  startImpersonation(
    userId: number,
    ttlMinutes: number = 60,
    location: number | null = null,
    reason?: string
  ): Observable<ImpersonationTokenResponse> {
    if (this.isImpersonating()) {
      return throwError(() => new Error('An impersonation session is already active'));
    }

    const payload: {
      user_id: number;
      ttl_minutes: number;
      location?: number | null;
      reason?: string;
    } = {
      user_id: userId,
      ttl_minutes: ttlMinutes,
      location: location,
    };

    if (reason) {
      payload.reason = reason;
    }

    // GUARDAR usuario admin original ANTES de impersonar
    const currentUser = this.authService.user;
    this.originalAdminUser = currentUser ? { ...currentUser } : null;
    // Persistir en sessionStorage para sobrevivir a refresh de página
    sessionStorage.setItem(IMPERSONATION_CONSTS.originalAdmin, JSON.stringify(this.originalAdminUser));

    // GUARDAR contexto de location del admin (admin view mode + location id)
    const adminContext = {
      isAdminViewMode: this.locationContextService.isAdminViewMode(),
      locationId: this.locationContextService.getCurrentLocationId(),
    };
    sessionStorage.setItem(IMPERSONATION_CONSTS.originalAdminContext, JSON.stringify(adminContext));

    return this.http.post<ImpersonationTokenResponse>(
      url_servicios + IMPERSONATION_URLS.START,
      payload
    ).pipe(
      tap((response) => {
        if (response.status === 'success') {
          this.setImpersonationState(response.data);
          // ACTUALIZAR AuthService con datos del usuario impersonado
          this.updateAuthServiceWithImpersonatedUser(response.data.user);
        }
      }),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Stop current impersonation session
   */
  stopImpersonation(reason?: string): Observable<ImpersonationStopResponse> {
    const impersonationId = this.state.impersonationId;

    if (!impersonationId) {
      return throwError(() => new Error('No active impersonation session'));
    }

    const payload: { impersonation_id: string; reason?: string } = {
      impersonation_id: impersonationId,
    };

    if (reason) {
      payload.reason = reason;
    }

    return this.http.post<ImpersonationStopResponse>(
      url_servicios + IMPERSONATION_URLS.STOP,
      payload
    ).pipe(
      tap(() => {
        this.clearImpersonationState();
      }),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Revoke a specific impersonation token (SUPERADMIN only)
   */
  revokeImpersonation(
    impersonationId: string,
    reason?: string
  ): Observable<ImpersonationStopResponse> {
    const payload: { impersonation_id: string; reason?: string } = {
      impersonation_id: impersonationId,
    };

    if (reason) {
      payload.reason = reason;
    }

    return this.http.post<ImpersonationStopResponse>(
      url_servicios + IMPERSONATION_URLS.REVOKE,
      payload
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get active impersonation sessions (SUPERADMIN only)
   */
  getActiveImpersonations(): Observable<ActiveImpersonationsResponse> {
    return this.http.get<ActiveImpersonationsResponse>(
      url_servicios + IMPERSONATION_URLS.ACTIVE
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Set impersonation state and save to sessionStorage
   */
  private setImpersonationState(data: ImpersonationTokenResponse['data']): void {
    const state: ImpersonationState = {
      isActive: true,
      impersonationId: data.impersonation_id,
      impersonatedUser: data.user,
      impersonatorUser: data.impersonator,
      expiresAt: new Date(data.expires_at),
      token: data.token,
    };

    // Save token separately for interceptor
    sessionStorage.setItem(IMPERSONATION_CONSTS.token, data.token);

    // Save full state
    sessionStorage.setItem(
      IMPERSONATION_CONSTS.state,
      JSON.stringify(state)
    );

    this.impersonationStateSubject.next(state);

    // Restart expiration check
    this.startExpirationCheck();
  }

  /**
   * Update AuthService with impersonated user data
   * This ensures the entire app sees the impersonated user's permissions
   */
  private updateAuthServiceWithImpersonatedUser(impersonatedUser: ImpersonatedUser): void {
    this.setAuthUserAndEmit({ ...impersonatedUser } as AuthUser);
  }

  /**
   * Clear impersonation state
   */
  clearImpersonationState(): void {
    // Stop expiration check
    this.stopExpirationCheck();

    const originalAdminJson = sessionStorage.getItem(IMPERSONATION_CONSTS.originalAdmin);
    const originalAdminContextJson = sessionStorage.getItem(IMPERSONATION_CONSTS.originalAdminContext);

    sessionStorage.removeItem(IMPERSONATION_CONSTS.token);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.state);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.originalAdmin);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.originalAdminContext);

    this.impersonationStateSubject.next({
      isActive: false,
      impersonationId: null,
      impersonatedUser: null,
      impersonatorUser: null,
      expiresAt: null,
      token: null,
    });

    // Restore original admin user
    if (!this.originalAdminUser) {
      if (originalAdminJson) {
        try {
          this.originalAdminUser = JSON.parse(originalAdminJson);
        } catch {
          this.originalAdminUser = null;
        }
      }
    }

    if (this.originalAdminUser) {
      this.setAuthUserAndEmit({ ...this.originalAdminUser });
    }
    this.originalAdminUser = null;

    // Restore admin location context
    this.restoreAdminLocationContext(originalAdminContextJson);
  }

  /**
   * Restore admin location context (admin view mode + location id) after impersonation ends
   */
  private restoreAdminLocationContext(contextJson: string | null): void {
    if (!contextJson) {
      this.locationContextService.setAdminViewMode(true);
      return;
    }

    try {
      const context: { isAdminViewMode: boolean; locationId: number | null } = JSON.parse(contextJson);
      if (context.isAdminViewMode) {
        this.locationContextService.setAdminViewMode(true);
      } else if (context.locationId !== null) {
        this.locationContextService.setCurrentLocationId(context.locationId);
      }
    } catch {
      this.locationContextService.setAdminViewMode(true);
    }
  }

  /**
   * Force clear impersonation state without restoring user
   * Used when session expires or on logout
   */
  forceClearImpersonationState(): void {
    this.stopExpirationCheck();

    const originalAdminContextJson = sessionStorage.getItem(IMPERSONATION_CONSTS.originalAdminContext);

    // Clear storage
    sessionStorage.removeItem(IMPERSONATION_CONSTS.token);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.state);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.originalAdmin);
    sessionStorage.removeItem(IMPERSONATION_CONSTS.originalAdminContext);

    // Reset state
    this.impersonationStateSubject.next({
      isActive: false,
      impersonationId: null,
      impersonatedUser: null,
      impersonatorUser: null,
      expiresAt: null,
      token: null,
    });

    // Clear original admin user reference
    this.originalAdminUser = null;

    // Restore admin location context
    this.restoreAdminLocationContext(originalAdminContextJson);
  }

  /**
   * Restore impersonation state from sessionStorage
   */
  private restoreImpersonationState(): void {
    const stateJson = sessionStorage.getItem(IMPERSONATION_CONSTS.state);
    const token = sessionStorage.getItem(IMPERSONATION_CONSTS.token);

    if (!stateJson || !token) {
      // No impersonation state to restore
      return;
    }

    // If admin token doesn't exist, user logged out - clear impersonation
    const adminToken = localStorage.getItem('token');
    if (!adminToken) {
      this.forceClearImpersonationState();
      return;
    }

    try {
      const state: ImpersonationState = JSON.parse(stateJson);

      // Check if token has expired
      if (state.expiresAt && new Date(state.expiresAt) < new Date()) {
        this.forceClearImpersonationState();
        return;
      }

      // Restore original admin user from sessionStorage
      const originalAdminJson = sessionStorage.getItem(IMPERSONATION_CONSTS.originalAdmin);
      if (originalAdminJson) {
        try {
          this.originalAdminUser = JSON.parse(originalAdminJson);
        } catch {
          // Silent fail - originalAdminUser will remain null
        }
      }

      // Restore state
      state.expiresAt = state.expiresAt ? new Date(state.expiresAt) : null;
      this.impersonationStateSubject.next(state);

      // Update AuthService with impersonated user
      if (state.impersonatedUser) {
        this.updateAuthServiceWithImpersonatedUser(state.impersonatedUser);
      }
    } catch {
      this.forceClearImpersonationState();
    }
  }

  /**
   * Check if impersonation token is expired
   */
  isTokenExpired(): boolean {
    const expiresAt = this.state.expiresAt;
    if (!expiresAt) return false;
    return new Date() >= expiresAt;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string;
    let errorCode = 'UNKNOWN_ERROR';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      errorMessage = error.error?.message || error.message;
      errorCode = error.error?.code || errorCode;

      // Handle specific impersonation errors
      if (
        errorCode === IMPERSONATION_CONSTS.ERROR_CODES.TOKEN_EXPIRED ||
        errorCode === IMPERSONATION_CONSTS.ERROR_CODES.TOKEN_REVOKED
      ) {
        this.clearImpersonationState();
      }
    }

    const impersonationError: ImpersonationError = {
      code: errorCode,
      message: errorMessage,
    };

    return throwError(() => impersonationError);
  }

  /** Handle impersonation expiration (called by interceptor) */
  handleTokenExpiration(): void {
    this.clearImpersonationState();
  }

  /** Handle impersonation revocation (called by interceptor) */
  handleTokenRevocation(): void {
    this.clearImpersonationState();
  }

  private startExpirationCheck(): void {
    this.stopExpirationCheck();

    this.expirationCheckInterval = setInterval(() => {
      if (this.isImpersonating() && this.isTokenExpired()) {
        this.handleAutoExpiration();
      }
    }, 1000);
  }

  /**
   * Stop expiration check interval
   */
  private stopExpirationCheck(): void {
    if (this.expirationCheckInterval) {
      clearInterval(this.expirationCheckInterval);
      this.expirationCheckInterval = null;
    }
  }

  /**
   * Handle automatic expiration (no user interaction)
   */
  private handleAutoExpiration(): void {
    this.clearImpersonationState();

    // Show elegant SweetAlert2 notification
    import('sweetalert2').then((Swal) => {
      Swal.default.fire({
        icon: 'warning',
        title: 'Session Expired',
        html: `
          <div style="text-align: left; padding: 10px 0;">
            <p><strong>Your impersonation session has expired.</strong></p>
            <p style="color: #6c757d; font-size: 14px; margin-top: 10px;">
              The session time limit has been reached. You will be automatically
              returned to your administrator account.
            </p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: 'impersonation-expired-popup',
          confirmButton: 'btn btn-primary'
        }
      }).then(() => {
        // Show loading transition
        Swal.default.fire({
          icon: 'info',
          title: 'Returning to Admin',
          html: 'Restoring your administrator session...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          timer: 1500,
          didOpen: () => {
            Swal.default.showLoading();
          }
        }).then(() => {
          this.router.navigate([AppRoutes.dashboard.dashboardUnique]);
        });
      });
    });
  }
}
